import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CalendarClock, MapPin, StickyNote, Wallet } from 'lucide-react';
import { T, companyAccent, companyInitials, fmtDate } from '../constants';

/**
 * A single application on the board. Draggable by grabbing anywhere on the
 * card; a plain click still opens details (dnd-kit only starts a drag after
 * the activation distance is exceeded, so the two do not conflict).
 */
export const JobCard = ({ job, onOpen }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: job._id,
    data: { stage: job.stage },
  });

  const accent = companyAccent(job.company);

  // A bookmarked job has not been applied to yet, so date it by when it was saved.
  const isBookmarked = job.stage === 'bookmarked';
  const savedOn   = fmtDate(job.createdAt);
  const appliedOn = fmtDate(job.appliedDate || job.createdAt);
  const dateLabel = isBookmarked
    ? (savedOn ? `Saved ${savedOn}` : 'Saved')
    : (appliedOn ? `Applied ${appliedOn}` : 'No date');

  const meta = [
    job.location     && { key: 'loc',  Icon: MapPin,        text: job.location },
    job.salary       && { key: 'sal',  Icon: Wallet,        text: job.salary },
    job.interviewDate && { key: 'int', Icon: CalendarClock, text: fmtDate(job.interviewDate), accent: '#EC4899' },
  ].filter(Boolean);

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => onOpen(job)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(job); } }}
      aria-label={`${job.title} at ${job.company}`}
      className="jt-card"
      style={{
        background: T.surface,
        border: `1px solid ${T.lineSoft}`,
        borderRadius: T.radius,
        padding: '12px 13px',
        cursor: 'grab',
        boxShadow: isDragging ? T.shadowHover : T.shadowCard,
        opacity: isDragging ? 0.4 : 1,
        fontFamily: T.font,
        touchAction: 'none',
        transition: 'box-shadow 0.18s, border-color 0.18s, transform 0.18s',
      }}
    >
      {/* Company row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 9, flexShrink: 0,
          background: `${accent}14`, border: `1px solid ${accent}28`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 800, color: accent, letterSpacing: 0.2,
        }}>
          {companyInitials(job.company)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 12.5, fontWeight: 700, color: T.ink,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {job.company}
          </div>
          <div style={{
            fontSize: 11.5, color: T.muted, marginTop: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {job.title}
          </div>
        </div>
        {job.notes ? (
          <StickyNote size={13} color={T.faint} style={{ flexShrink: 0 }} aria-label="Has notes" />
        ) : null}
      </div>

      {/* Meta chips */}
      {meta.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 9 }}>
          {meta.map(({ key, Icon, text, accent: chipAccent }) => (
            <span key={key} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10.5, fontWeight: 600,
              color: chipAccent || T.muted,
              background: chipAccent ? `${chipAccent}12` : T.canvas,
              border: `1px solid ${chipAccent ? `${chipAccent}28` : T.line}`,
              borderRadius: 6, padding: '2px 7px', maxWidth: '100%',
            }}>
              <Icon size={10} strokeWidth={2.2} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span>
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 10.5, color: T.faint }}>
          {dateLabel}
        </span>
        <span
          onClick={(e) => { e.stopPropagation(); onOpen(job); }}
          style={{
            fontSize: 11, fontWeight: 700, color: T.brand,
            background: T.brandSoft, border: `1px solid ${T.brandBorder}`,
            borderRadius: 6, padding: '2px 10px', cursor: 'pointer', flexShrink: 0,
          }}
        >
          View
        </span>
      </div>
    </div>
  );
};
